import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Siren } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { INCIDENT_CATEGORY_LABELS, INCIDENT_STATUS_COLORS, INCIDENT_STATUS_LABELS, SEVERITY_COLORS } from "@/config/incidentCatalog";

export default function IncidentsTab({ child }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/incidents").then((r) => {
      setItems((r.data.items || []).filter((i) => i.child_id === child.id));
    }).catch((e) => toast.error(formatApiError(e)));
  }, [child.id]);

  return (
    <Card data-testid="incidents-tab">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg flex items-center gap-2"><Siren className="h-5 w-5 text-red-600" /> Incidents</CardTitle>
        <Link to={`/incidents/new?child=${child.id}`} data-testid="incident-create-button"
          className="inline-flex h-11 items-center gap-2 rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 transition-colors duration-200">
          <Plus className="h-4 w-4" /> Report incident
        </Link>
      </CardHeader>
      <CardContent>
        {child.has_safeguarding_record && (
          <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="safeguarding-indicator">
            Restricted Safeguarding Record Exists — visible to authorised safeguarding staff only.
          </p>
        )}
        {items === null ? (
          <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-600" data-testid="incidents-empty">No incidents recorded for this case.</p>
        ) : (
          <div className="space-y-2">
            {items.map((i) => (
              <Link key={i.id} to={`/incidents/${i.id}`} data-testid={`incident-row-${i.incident_number}`}
                className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3 hover:bg-muted transition-colors duration-200">
                <div className="min-w-[140px]">
                  <p className="font-mono font-semibold text-slate-900">{i.incident_number}</p>
                  <p className="text-xs text-slate-500">{i.incident_date} · {i.reporter_name}</p>
                </div>
                <p className="text-xs text-slate-600 flex-1">{INCIDENT_CATEGORY_LABELS[i.category] || i.category || "—"}</p>
                <div className="flex items-center gap-2">
                  {i.severity === "critical" && <Badge className={SEVERITY_COLORS.critical}>CRITICAL</Badge>}
                  {!i.detail_complete && <Badge className="bg-amber-100 text-amber-800 border-transparent">Follow-up required</Badge>}
                  <Badge className={INCIDENT_STATUS_COLORS[i.status]}>{INCIDENT_STATUS_LABELS[i.status]}</Badge>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
